/**
 * A composição das dependências que vivem FORA da árvore de componentes.
 *
 * ⚠️ **Existe porque a ligação e o teste da ligação divergiram.** Antes, o
 * `App.tsx` criava o QueryClient (que registra o próprio `clear()` como limpeza
 * de logout) e mais nada, enquanto a suíte montava o router direto. Resultado: a
 * limpeza do tenant persistido **nunca foi registrada em lugar nenhum**, o
 * comentário de `SessionProvider.sair()` que dizia "tenant persistido em 0.5"
 * descrevia código que não existia, e o teste que "provava" o mecanismo
 * registrava as limpezas à mão — testando o registro, e não a ligação.
 *
 * O sintoma chegou pelo Fábio em 2026-08-21: sair como Arquiteto e entrar como
 * Cliente no mesmo tab deixava `iox.cliente = tribunal-de-justica` no
 * armazenamento.
 *
 * Agora existe UMA função, usada por `App.tsx` e por `test/providers.tsx`. Uma
 * dependência nova entra aqui e passa a ser exercitada pela suíte no mesmo dia.
 */
import type { QueryClient } from '@tanstack/react-query'

import { criarQueryClient } from '@/api/queryClient'
import { registrarLimpezaDeSessao } from '@/auth/sessionBridge'
import { esquecerCliente } from '@/tenant/persistencia'

export function montarDependencias(): { queryClient: QueryClient } {
  // Criar o client já registra `queryClient.clear()` como limpeza — ver
  // `api/queryClient.ts`.
  const queryClient = criarQueryClient()

  /**
   * 🔒 O tenant selecionado morre com a sessão.
   *
   * `clienteLembrado()` já revalida contra a lista do usuário, então um tenant
   * sobrevivente não conseguiria ser RESTAURADO indevidamente. Mas ele continua
   * legível no `sessionStorage` — e, numa máquina compartilhada, isso conta ao
   * próximo usuário em qual cliente o anterior estava trabalhando. Esquecer é a
   * diferença entre "não dá para usar" e "não está lá".
   */
  registrarLimpezaDeSessao(esquecerCliente)

  return { queryClient }
}
